import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import { Button, IconButton } from '@mui/material';
import Popover from '@mui/material/Popover';
import { Box } from '@mui/system';
import { useCallback, useState } from 'react';
import {
  HexAlphaColorPicker,
  HexColorPicker,
  RgbColor,
  RgbColorPicker,
} from 'react-colorful';

type HexColourPickerProps = {
  type: 'hex';
  colour: string;
  onChange: (colour: string) => void;
  defaultColour?: string;
  disabled?: boolean;
  iconDefaultColour?: string;
  title?: string;
  backgroundColour?: string;
  hoverColour?: string;
  borderStyle?: string;
  onClose?: (colour: string) => void;
};

type HexAlphaColourPickerProps = {
  type: 'hexAlpha';
  colour: string;
  onChange: (colour: string) => void;
  defaultColour?: string;
  disabled?: boolean;
  iconDefaultColour?: string;
  title?: string;
  backgroundColour?: string;
  hoverColour?: string;
  borderStyle?: string;
  onClose?: (colour: string) => void;
};

type RgbColourPickerProps = {
  type: 'rgb';
  colour: RgbColor;
  onChange: (colour: RgbColor) => void;
  defaultColour?: RgbColor;
  disabled?: boolean;
  iconDefaultColour?: string;
  title?: string;
  backgroundColour?: string;
  hoverColour?: string;
  borderStyle?: string;
  onClose?: (colour: RgbColor) => void;
};

type ColourPickerProps =
  | HexColourPickerProps
  | RgbColourPickerProps
  | HexAlphaColourPickerProps;

function ColourPicker({
  colour,
  onChange,
  type,
  disabled,
  iconDefaultColour,
  title,
  backgroundColour,
  hoverColour,
  borderStyle,
}: ColourPickerProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [localColour, setLocalColour] = useState<string>(
    typeof colour === 'string' ? colour : JSON.stringify(colour)
  );
  const [tempColour, setTempColour] = useState<string>(localColour);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setTempColour(localColour);
  };

  const handleSubmit = () => {
    if (type === 'rgb' && typeof colour === 'object') {
      const newColour = JSON.parse(localColour) as RgbColor;
      onChange(newColour);
    } else {
      onChange(type === 'rgb' ? JSON.parse(localColour) : localColour);
    }
    setAnchorEl(null);
  };

  const handleCancel = () => {
    setLocalColour(tempColour);
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const isSubmitDisabled = localColour === tempColour;

  const iconColor = useCallback(() => {
    if (disabled) return 'inherit';

    try {
      if (type === 'rgb' && typeof colour === 'object') {
        const parsed = JSON.parse(localColour);
        return `rgb(${parsed.r}, ${parsed.g}, ${parsed.b})`;
      }
    } catch {
      return iconDefaultColour || 'inherit';
    }

    return localColour || iconDefaultColour || 'inherit';
  }, [disabled, localColour, iconDefaultColour, type, colour]);

  return (
    <>
      <IconButton
        title={title}
        onClick={handleClick}
        data-testid="colour-picker"
        disabled={disabled}
        sx={{
          backgroundColor: backgroundColour,
          border: borderStyle,
          borderColor: borderStyle,
          '&:hover': {
            backgroundColor: hoverColour,
          },
        }}
      >
        <FormatColorFillIcon style={{ color: iconColor() }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              overflow: 'hidden',
              padding: '14px 14px',
              borderRadius: 2,
            },
          },
        }}
      >
        {(() => {
          switch (type) {
            case 'hex':
              return (
                <HexColorPicker
                  color={localColour}
                  onChange={(newColour) => setLocalColour(newColour)}
                />
              );
            case 'hexAlpha':
              return (
                <HexAlphaColorPicker
                  color={localColour}
                  onChange={(newColour) => setLocalColour(newColour)}
                />
              );
            case 'rgb':
              return (
                <RgbColorPicker
                  color={JSON.parse(localColour)}
                  onChange={(newColour) =>
                    setLocalColour(JSON.stringify(newColour))
                  }
                />
              );
            default:
              return null;
          }
        })()}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 1,
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
            data-testid="cancel-colour-button"
            sx={{ width: '48%' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            data-testid="submit-colour-button"
            sx={{ width: '48%' }}
            disabled={isSubmitDisabled}
          >
            Submit
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default ColourPicker;

ColourPicker.defaultProps = {
  defaultColour: undefined,
  disabled: false,
  iconDefaultColour: undefined,
  title: undefined,
  backgroundColour: undefined,
  hoverColour: undefined,
  onClose: undefined,
  borderStyle: undefined,
};
